import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";

const EMPTY_FORM = { schoolName: "", contactName: "", designation: "", email: "", phone: "", city: "", studentCount: "", board: "", message: "" };
export default function SchoolDemoRequest() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    schoolName: searchParams.get("schoolName") || "",
    board: searchParams.get("board") || "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitError("");
    const payload = {
      ...Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()])),
      studentCount: Number(form.studentCount || 0),
      source: "website",
    };
    if (!payload.board) {
      setSubmitError("Board / curriculum: must not be blank");
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post(API_PATHS.schoolLeads.create(), payload);
      if (response.status !== 201 || response.data?.success !== true) {
        const error = new Error("The server did not confirm your demo request. Please try again.");
        error.response = response;
        throw error;
      }
      toast.success("Your demo request is in. Our school success team will contact you shortly.");
      setForm(EMPTY_FORM);
    } catch (error) {
      const body = error.response?.data;
      const fieldErrors = body?.data && typeof body.data === "object"
        ? Object.entries(body.data)
          .filter(([key, value]) => key in EMPTY_FORM && typeof value === "string")
          .map(([key, value]) => `${key === "board" ? "Board / curriculum" : key}: ${value}`)
          .join(". ")
        : "";
      const message = fieldErrors || (typeof body?.message === "string" && body.success === false ? body.message : "Your demo request could not be confirmed. Please try again.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    ["schoolName", "School name", "text"], ["contactName", "Your name", "text"], ["designation", "Your role", "text"],
    ["email", "Work email", "email"], ["phone", "Phone number", "tel"], ["city", "City", "text"],
    ["studentCount", "Number of students", "number"], ["board", "Board / curriculum", "text"],
  ];

  return <main className="min-h-screen bg-slate-950 px-4 py-12 text-white sm:px-6"><div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
    <section><Link to="/" className="text-sm font-semibold text-cyan-300 hover:text-cyan-200">← Back to ClassNX</Link><p className="mt-8 text-xs font-bold uppercase tracking-[0.24em] text-cyan-300">For school leaders</p><h1 className="mt-4 text-4xl font-black leading-tight sm:text-5xl">Bring every school operation into one calm, connected workspace.</h1><p className="mt-5 max-w-xl text-lg leading-8 text-slate-300">See how ClassNX can simplify admissions, academics, fees, parent communication, and reporting for your school.</p><div className="mt-8 grid gap-3 text-sm text-slate-200 sm:grid-cols-2"><p>✓ Personal platform walkthrough</p><p>✓ Implementation plan for your school</p><p>✓ No-obligation consultation</p><p>✓ Answers within one business day</p></div></section>
    <section className="rounded-3xl border border-slate-700 bg-white p-6 text-slate-900 shadow-2xl sm:p-8"><p className="text-sm font-bold uppercase tracking-[0.18em] text-cyan-700">Request a demo</p><h2 className="mt-2 text-2xl font-black">Tell us about your school</h2><form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2">{fields.map(([key, label, type]) => <label key={key} className={key === "schoolName" || key === "contactName" ? "sm:col-span-1" : ""}><span className="mb-1 block text-sm font-semibold">{label}</span><input required={key !== "designation"} type={type} min={type === "number" ? "1" : undefined} value={form[key]} onChange={(event) => update(key, event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100" /></label>)}<label className="sm:col-span-2"><span className="mb-1 block text-sm font-semibold">What would you like to improve? <span className="font-normal text-slate-400">(optional)</span></span><textarea rows="3" value={form.message} onChange={(event) => update("message", event.target.value)} className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100" /></label><div className="sm:col-span-2">{submitError && <p role="alert" className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>}<button disabled={submitting} className="w-full rounded-xl bg-cyan-600 px-5 py-3 font-bold text-white transition hover:bg-cyan-700 disabled:opacity-60">{submitting ? "Sending request…" : "Request my school demo"}</button><p className="mt-3 text-center text-xs text-slate-500">By submitting, you agree that ClassNX may contact you about this request.</p></div></form></section>
  </div></main>;
}
